//-- Copyright (c) 2021 TMT Insights Group, LLC
import PolarisIcon from '@/components/_common/PolarisIcon';
import { Box, Button, Grid, MenuItem, TextField } from '@mui/material';
import React, { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { RJSFSchema } from '@rjsf/utils';
import LanguageSelector from '@/components/_common/LanguageSelector/LanguageSelector';
import Fieldset from '@/components/_common/Fieldset';
import Modal from '@/components/_common/Modal/Modal';
import SchemaForm from '@/components/_common/SchemaForm/SchemaForm';
import { type CustomerProfile, type CustomerProfileData } from '@/redux/slices/slice_types';
import { selectAudioLanguages, selectMediaResolutions, selectSubtitlesLanguages } from '@/redux/selectors/constantsSelectors';
import { findandFormatUTCDateInString } from '@/_utils/date';
import { useAppSelector } from '@/redux/hooks';

export type EditModalProfileData = Pick<CustomerProfileData, 'media_resolution' | 'audio_tracks' | 'subtitle_tracks' | 'workflow'>;

interface EditModalProfile {
  name: CustomerProfile['name'];
  data: EditModalProfileData;
}

type OPProfileDetailsEditModalProps = {
  open: boolean;
  profile: EditModalProfile;
  orderSpecSchema?: RJSFSchema;
  orderSpecInputs?: Record<string, any>;
  orderSpecSchemaFormBeforeMessage?: React.ReactNode;
  onClose: () => void;
  onSave: (updatedValues: EditModalProfileData) => void;
  updateOrderSpecInputs(inputs: Record<string, any>): void;
};

// TODO: this component renders whether orderSpecSchema form or the default Resolution/Audio/Subtitles form
//  it would be great to move those two forms into separate components.
const OPProfileDetailsOutputEditModal = ({
  open,
  profile,
  orderSpecSchema,
  orderSpecInputs,
  orderSpecSchemaFormBeforeMessage,
  onClose,
  onSave,
  updateOrderSpecInputs
}: OPProfileDetailsEditModalProps) => {
  const [updatedValues, setUpdatedValues] = useState<EditModalProfileData>(profile?.data);
  const [orderSpecInputsInternal, setOrderSpecInputsInternal] = useState<Record<string, any> | null>(orderSpecInputs || null);
  const AudioLanguages = useAppSelector(selectAudioLanguages);
  const SubtitleLanguages = useAppSelector(selectSubtitlesLanguages);
  const mediaResolutions = useAppSelector(selectMediaResolutions);
  const formRef = useRef<any>(null);
  const showOrderSpecSchemaForm = orderSpecSchema?.properties && Object.keys(orderSpecSchema.properties).length;

  useEffect(() => {
    setUpdatedValues(profile?.data);
  }, [profile?.data]);

  const schemaFormKey = useMemo(
    () => `${open ? 'open' : 'closed'}:${orderSpecInputs ? JSON.stringify(orderSpecInputs) : 'empty'}`,
    [open, orderSpecInputs]
  );

  useEffect(() => {
    if (open && orderSpecInputs) {
      const cloned = JSON.parse(JSON.stringify(orderSpecInputs));

      setOrderSpecInputsInternal(cloned);
    }
    if (!open) {
      setOrderSpecInputsInternal(null);
    }
  }, [open, orderSpecInputs]);

  const handleChange = (e: ChangeEvent<any>) => {
    const { name, value } = e.target;
    setUpdatedValues((prev) => ({ ...prev, [name]: value }));
  };

  const onCancel = () => {
    onClose();
    setTimeout(() => {
      setUpdatedValues(profile?.data);
      setOrderSpecInputsInternal(null);
    }, 700);
  };

  const handleSave = () => {
    if (showOrderSpecSchemaForm) {
      if (formRef.current && !formRef.current.validateForm()) return;
      if (orderSpecInputsInternal) {
        updateOrderSpecInputs(orderSpecInputsInternal);
      }
    } else {
      onSave(updatedValues);
    }
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={
        <>
          <strong>Output Edit:</strong> {findandFormatUTCDateInString(profile.name)}
        </>
      }
      icon={<PolarisIcon icon="edit" width={13} />}
    >
      {showOrderSpecSchemaForm ? (
        <>
          {orderSpecSchemaFormBeforeMessage}
          <SchemaForm
            key={schemaFormKey}
            tagName="div"
            schema={orderSpecSchema}
            formData={orderSpecInputsInternal ?? orderSpecInputs ?? {}}
            onChange={(data: any) => setOrderSpecInputsInternal(data.formData)}
            formRef={formRef}
          />
        </>
      ) : (
        <>
          <Box sx={{ mb: 3 }}>
            <Fieldset label="Media" color="primary">
              <Grid container item xs={12} sm={6} md={3}>
                <TextField
                  select
                  fullWidth
                  variant="filled"
                  name="media_resolution"
                  value={updatedValues.media_resolution || ''}
                  label="Resolution"
                  onChange={handleChange}
                >
                  {mediaResolutions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Fieldset>
          </Box>
          <Box sx={{ mb: 3 }}>
            <Fieldset label="Audio" color="secondary">
              <LanguageSelector
                id="audio_tracks"
                color="secondary"
                languageCodes={updatedValues.audio_tracks}
                onChange={(codes: string[]) => setUpdatedValues({ ...updatedValues, audio_tracks: codes })}
                languages={AudioLanguages}
              />
            </Fieldset>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Fieldset label="Subtitles" color="default">
              <LanguageSelector
                id="subtitle_tracks"
                color="default"
                languageCodes={updatedValues.subtitle_tracks}
                onChange={(codes: string[]) => setUpdatedValues({ ...updatedValues, subtitle_tracks: codes })}
                languages={SubtitleLanguages}
              />
            </Fieldset>
          </Box>
        </>
      )}

      {/* save button */}
      <Grid container justifyContent="flex-end" sx={{ mt: 5 }}>
        <Button onClick={onCancel} color="default" variant="text" sx={{ mr: 2 }}>
          Cancel
        </Button>
        <Button onClick={handleSave} color="primary" variant="contained">
          Save
        </Button>
      </Grid>
    </Modal>
  );
};

export default OPProfileDetailsOutputEditModal;
